import {
  Box,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  createTheme,
  Grid,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ThemeProvider,
  Typography,
} from '@mui/material'
import ArrowRightIcon from '@mui/icons-material/ArrowRight'
import {
  publicKey as borshPublicKey,
  u64,
  i64,
  u8,
  struct,
} from '@project-serum/borsh'
import { useConnection, useWallet } from '@solana/wallet-adapter-react'
import { AccountInfo, LAMPORTS_PER_SOL, PublicKey } from '@solana/web3.js'
import { FC, useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { LoansList } from '../../types'
import {
  LOAN_LIST_SCREEN_MIN_WIDTH_FOR_ROWS_DISPLAY,
  RAD_ID,
} from '../../utils/constants'
import { CloseLoan } from '../CloseLoan/CloseLoan'
import {
  checkTimeout,
  getLoans,
  setLoansList,
  setNotifier,
  setScanTimer,
} from '../../redux/modules/loans'
import { useNotify } from '../../utils/notify'
import { numberWithCommas } from '../../utils/helpers'
import { SxProps } from '@mui/system'
import { OpenLoanButton } from '../OpenLoan/OpenLoanButton'
import { getScreen } from '../../redux/modules/app'

function getBigNumber(num: any) {
  return num === undefined || num === null ? 0 : parseFloat(num.toString())
}

let dataLayout = struct([
  u8('version'),
  borshPublicKey('owner'),
  u64('collateral_amount'),
  u64('market_price'),
  u64('loan_amount'),
  borshPublicKey('loan_mint'),
  i64('loan_creation_date'),
  borshPublicKey('vault'),
])

export const LoanList: FC = () => {
  const { connection } = useConnection()
  const { publicKey } = useWallet()
  const dispatch = useDispatch()
  const loans = useSelector(getLoans)
  const componentIsMounted = useRef(true)
  const notify = useNotify()
  const { windowWidth } = useSelector(getScreen)
  const smallScreen = windowWidth < LOAN_LIST_SCREEN_MIN_WIDTH_FOR_ROWS_DISPLAY

  if (!loans.notify) {
    dispatch(setNotifier(notify))
  }

  if (publicKey && !loans.walletKey?.equals(publicKey)) {
    dispatch(setLoansList([], publicKey))
  }

  useEffect(() => {
    return () => {
      dispatch(setScanTimer(null, null))
      componentIsMounted.current = false
    }
  }, [dispatch])

  const processAccountsList = (
    accounts: Array<{
      pubkey: PublicKey
      account: AccountInfo<Buffer>
    }>
  ) => {
    let loanList: LoansList[] = []
    for (var i = 0; i < accounts.length; i++) {
      const data = accounts[i].account.data
      const layout = dataLayout.decode(Buffer.from(data))

      const timestamp = getBigNumber(layout.loan_creation_date) * 1000
      const dateTxt = new Date(timestamp).toLocaleDateString('en-us')

      loanList.push({
        loanAccount: accounts[i].pubkey,
        collateral: getBigNumber(layout.collateral_amount),
        marketPrice: getBigNumber(layout.market_price),
        loanAmount: getBigNumber(layout.loan_amount),
        dateOpened: dateTxt,
        timestamp: timestamp,
      })
    }

    if (loanList.length) {
      dispatch(setLoansList(loanList, publicKey))
    } else {
      dispatch(setLoansList(null, publicKey))
    }
  }

  useEffect(() => {
    async function getAccounts() {
      if (publicKey) {
        const accounts = connection.getProgramAccounts(RAD_ID, {
          commitment: 'max',
          filters: [
            {
              memcmp: {
                bytes: publicKey.toString(),
                offset: 1,
              },
            },
          ],
        })

        if (componentIsMounted.current) {
          accounts.then((accounts) => processAccountsList(accounts))
        }
      } else {
        notify('warning', 'Wallet Not Connected')
      }
    }

    // GET LOAN LIST
    if (publicKey && loans.list?.length === 0) {
      getAccounts()
    }
  })

  useEffect(() => {
    async function checkAccountsTotal(loansList: LoansList[] | null) {
      const currentLoanTotal = loansList ? loansList.length : 0

      if (publicKey) {
        const accounts = connection.getProgramAccounts(RAD_ID, {
          commitment: 'max',
          filters: [
            {
              memcmp: {
                bytes: publicKey.toString(),
                offset: 1,
              },
            },
          ],
        })

        if (componentIsMounted.current) {
          accounts.then((accounts) => {
            dispatch(checkTimeout())
            if (accounts.length !== currentLoanTotal) {
              processAccountsList(accounts)
            }
          })
        }
      } else {
        notify('warning', 'Wallet Not Connected')
      }
    }

    // SCAN FOR CHANGES TO ACCOUNTS TOTAL
    if (publicKey && loans.scan && loans.scanTimer === null) {
      dispatch(
        setScanTimer(
          new Date().getTime(),
          setInterval(() => checkAccountsTotal(loans.list), 5000)
        )
      )
    }
  })

  const theme = createTheme({
    components: {
      MuiTableCell: {
        styleOverrides: {
          root: {
            color: 'white',
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            backgroundColor: 'transparent',
          },
        },
      },
    },
  })

  if (loans.scan) {
    return (
      <>
        <Typography component="div">
          <Box
            className="Updating-loan-list-text"
            sx={{ fontSize: 'h5.fontSize', m: 1 }}
          >
            Updating Loan List
          </Box>
          <div>
            <img
              className={'Dancing-banana'}
              src="/dancing-banana.gif"
              alt="Updating Loan List"
            />
          </div>
        </Typography>
        <br />
        <LinearProgress sx={{ width: '30%', marginLeft: '35%' }} />
      </>
    )
  } else if (loans.list?.length) {
    let loansUnsorted: LoansList[] = Object.assign([], loans.list)
    let loansSorted: LoansList[] = loansUnsorted.sort((a, b) => {
      if (a.timestamp > b.timestamp) {
        return -1
      } else if (a.timestamp < b.timestamp) {
        return 1
      } else {
        return 0
      }
    })
    if (smallScreen) {
      const muiTableCellOverride: SxProps = {
        borderBottom: '0px',
        padding: '0px',
      }
      return (
        <>
          <OpenLoanButton />
          <br />
          <ThemeProvider theme={theme}>
            {loansSorted.map((row) => (
              <div key={row.timestamp}>
                <Card
                  sx={{
                    bgcolor: 'transparent',
                    border: '1px solid rgb(76, 136, 86)',
                  }}
                  variant="outlined"
                  raised={false}
                >
                  <CardContent sx={{ padding: '5px' }}>
                    <TableContainer>
                      <Table aria-label="simple table">
                        <tbody>
                          <TableRow>
                            <TableCell sx={muiTableCellOverride}>
                              Collateral Amount (SOL):
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell
                              sx={muiTableCellOverride}
                              className="Loan-list-cell-value"
                              component="th"
                              scope="row"
                            >
                              <Grid container direction="row" alignItems="left">
                                <ArrowRightIcon />
                                <span style={{ marginTop: '2px' }}>
                                  {row.collateral / LAMPORTS_PER_SOL}
                                </span>
                              </Grid>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={muiTableCellOverride} align="left">
                              SOL/USD Market Price:
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell
                              sx={muiTableCellOverride}
                              className="Loan-list-cell-value"
                              align="left"
                            >
                              <Grid container direction="row" alignItems="left">
                                <ArrowRightIcon />
                                <span style={{ marginTop: '2px' }}>
                                  ${row.marketPrice}
                                </span>
                              </Grid>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={muiTableCellOverride} align="left">
                              Loan Amount:
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell
                              sx={muiTableCellOverride}
                              className="Loan-list-cell-value"
                              align="left"
                            >
                              <Grid container direction="row" alignItems="left">
                                <ArrowRightIcon />
                                <span style={{ marginTop: '2px' }}>
                                  {numberWithCommas(
                                    Math.round(
                                      row.loanAmount / LAMPORTS_PER_SOL
                                    )
                                  )}
                                </span>
                              </Grid>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={muiTableCellOverride} align="left">
                              Date Opened:
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell
                              sx={muiTableCellOverride}
                              className="Loan-list-cell-value"
                              align="left"
                            >
                              <Grid container direction="row" alignItems="left">
                                <ArrowRightIcon />
                                <span style={{ marginTop: '2px' }}>
                                  {row.dateOpened}
                                </span>
                              </Grid>
                            </TableCell>
                          </TableRow>
                        </tbody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                  <CardActions>
                    <CloseLoan
                      loanAccount={row.loanAccount}
                      loanAmount={row.loanAmount}
                      buttonSize="small"
                      text="Close Loan"
                    />
                  </CardActions>
                </Card>
                <br />
              </div>
            ))}
          </ThemeProvider>
        </>
      )
    } else {
      return (
        <>
          <OpenLoanButton />
          <br />
          <ThemeProvider theme={theme}>
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell>Collateral Amount (SOL)</TableCell>
                    <TableCell align="right">SOL/USD Market Price</TableCell>
                    <TableCell align="right">Loan Amount</TableCell>
                    <TableCell align="right">Date Opened</TableCell>
                    <TableCell align="right"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loansSorted.map((row) => (
                    <TableRow
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      key={row.timestamp}
                    >
                      <TableCell component="th" scope="row">
                        {row.collateral / LAMPORTS_PER_SOL}
                      </TableCell>
                      <TableCell align="right">${row.marketPrice}</TableCell>
                      <TableCell align="right">
                        {numberWithCommas(
                          Math.round(row.loanAmount / LAMPORTS_PER_SOL)
                        )}
                      </TableCell>
                      <TableCell align="right">{row.dateOpened}</TableCell>
                      <TableCell align="right">
                        <CloseLoan
                          loanAccount={row.loanAccount}
                          loanAmount={row.loanAmount}
                          buttonSize="small"
                          text="Close Loan"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </ThemeProvider>
        </>
      )
    }
  } else if (loans.list == null) {
    if (smallScreen) {
      return (
        <>
          <OpenLoanButton />
          <Typography component="div">
            <Box sx={{ fontSize: 'h5.fontSize', m: 1 }}>No Open Loans</Box>
            <img className="Banana-peel" src="/BananaPeel.png" alt="No Loans" />
          </Typography>
        </>
      )
    } else {
      return (
        <>
          <OpenLoanButton />
          <Typography component="div">
            <Box sx={{ fontSize: 'h5.fontSize', m: 1 }}>
              You Have No Loans Currently Open With Wallet ID:
            </Box>
            <Box sx={{ fontSize: 'h8.fontSize', m: 1 }}>
              {publicKey?.toString()}
            </Box>
            <img className="Banana-peel" src="/BananaPeel.png" alt="No Loans" />
          </Typography>
        </>
      )
    }
  } else {
    return <CircularProgress />
  }
}
